import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Mic, Square, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";

const MIME_CANDIDATES = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"];

const clock = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

export const VoiceRecorder = ({ onAudio, busy }) => {
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  useEffect(() => () => clearInterval(timerRef.current), []);

  const start = async () => {
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      return toast.error("This browser cannot record — upload an audio file instead");
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MIME_CANDIDATES.find((t) => window.MediaRecorder.isTypeSupported(t));
      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      chunksRef.current = [];
      recorder.ondataavailable = (e) => e.data.size && chunksRef.current.push(e.data);
      recorder.onstop = () => {
        stream.getTracks().forEach((t) => t.stop());
        const type = recorder.mimeType || "audio/webm";
        const ext = type.includes("mp4") ? "m4a" : "webm";
        const blob = new Blob(chunksRef.current, { type });
        if (blob.size < 800) return toast.error("That recording was too short");
        onAudio(new File([blob], `voice-note.${ext}`, { type }));
      };
      recorder.start();
      recorderRef.current = recorder;
      setRecording(true);
      setSeconds(0);
      timerRef.current = setInterval(() => setSeconds((s) => s + 1), 1000);
    } catch {
      toast.error("Microphone blocked — allow access or upload an audio file");
    }
  };

  const stop = () => {
    clearInterval(timerRef.current);
    recorderRef.current?.stop();
    setRecording(false);
  };

  return (
    <div className="flex flex-wrap items-center gap-3" data-testid="voice-recorder">
      {recording ? (
        <Button
          data-testid="voice-stop-btn"
          onClick={stop}
          className="rounded-full h-12 px-6 bg-destructive text-destructive-foreground hover:bg-destructive/90"
        >
          <Square className="h-4 w-4 mr-2" /> Stop · {clock(seconds)}
        </Button>
      ) : (
        <Button
          data-testid="voice-record-btn"
          onClick={start}
          disabled={busy}
          className="rounded-full h-12 px-6"
        >
          <Mic className="h-5 w-5 mr-2" /> {busy ? "Transcribing…" : "Record note"}
        </Button>
      )}

      <label
        className={`inline-flex items-center gap-2 h-12 px-5 rounded-full border border-border text-base font-medium cursor-pointer transition-colors duration-200 hover:bg-accent ${
          busy || recording ? "opacity-50 pointer-events-none" : ""
        }`}
        data-testid="voice-upload-label"
      >
        <Upload className="h-5 w-5" /> Upload audio
        <input
          type="file"
          accept="audio/webm,audio/mp4,audio/m4a,audio/mpeg,audio/wav,.webm,.m4a,.mp3,.wav"
          data-testid="voice-upload-input"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            e.target.value = "";
            if (file) onAudio(file);
          }}
        />
      </label>

      {recording ? (
        <span className="inline-flex items-center gap-2 text-sm text-muted-foreground">
          <span className="h-2.5 w-2.5 rounded-full bg-destructive animate-pulse" /> Listening…
        </span>
      ) : null}
    </div>
  );
};
